import { useEffect, useState } from "react";
import { fetchPage } from "../lib/api";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import PageSections from "../components/PageSections";
import EnquiryForm from "../components/EnquiryForm";

const FALLBACK_IMG = "https://images.pexels.com/photos/14816709/pexels-photo-14816709.jpeg";

export default function About() {
  const [page, setPage] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  useEffect(() => {
    fetchPage("about").then(setPage).catch(() => {}).finally(() => setPageLoaded(true));
  }, []);

  const sections = page?.sections || [];
  const intro = sections.find(s => s.type === "intro");
  const arabic = sections.find(s => s.type === "richtext" && /arabic/i.test(s.title || ""));
  // Everything else renders normally below the hero block.
  const otherSections = sections.filter(s => s !== intro && s !== arabic);

  return (
    <>
      <Seo
        title={page?.meta_title || "About — Lime Driving School"}
        description={page?.meta_description}
        robots={page?.meta_robots}
        jsonLd={page?.schema_json}
      />
      <PageHero
        eyebrow="About"
        title={page?.title || "About Lime Driving School"}
        subtitle="Meet Akram — the calmest driving instructor in Southampton. Calm, patient, structured."
      />

      <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="about-intro-block">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          {/* LEFT — instructor image (CMS-driven via the About page's intro block) */}
          <div className="md:col-span-5">
            {pageLoaded ? (
              <img src={intro?.image || FALLBACK_IMG}
                   alt={intro?.image_alt || intro?.title || "Lime Driving School instructor"}
                   className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] object-cover animate-[fadein_400ms_ease-out]"
                   data-testid="about-intro-image" />
            ) : (
              <div className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] bg-[#F3F4F6]" />
            )}
          </div>

          {/* RIGHT — Akram text → enquiry form → Learn in Arabic */}
          <div className="md:col-span-7 space-y-10">
            <div>
              {intro?.eyebrow && (
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">{intro.eyebrow}</span>
              )}
              {intro?.title && (
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium mt-3"
                    style={{fontFamily:"Outfit, sans-serif"}}>{intro.title}</h2>
              )}
              {intro?.body && (
                <div className="mt-5 prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: intro.body }} />
              )}
            </div>

            <EnquiryForm testIdPrefix="about-enquiry" compact showHeading />

            {arabic && (
              <div data-testid="about-arabic-block">
                <h3 className="text-xl sm:text-2xl tracking-tight font-medium"
                    style={{fontFamily:"Outfit, sans-serif"}}>{arabic.title}</h3>
                <div className="mt-3 prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: arabic.body || "" }} />
              </div>
            )}
          </div>
        </div>
      </section>

      <div data-testid="about-page">
        <PageSections sections={otherSections} />
      </div>
    </>
  );
}
